import { useEffect, useMemo, useRef } from 'react';

import { useDebouncedValue } from '@mantine/hooks';
import type { SalesPortalCheckoutProgressWrite } from 'types';

import type { CheckoutFormValues } from './CheckoutContext';
import type { CheckoutPhase, CheckoutStep } from './checkoutFlowStates';
import { buildCheckoutProgressPayload } from './checkoutProgressPayload';

// Debounce for the server-side progress save. Long enough to coalesce a burst of
// typing into one PUT, short enough that a buyer who closes the tab right after
// finishing a step still has that step on record.
export const CHECKOUT_PROGRESS_SYNC_DEBOUNCE_MS = 1500;

interface UseCheckoutProgressSyncArgs {
  // A signed-in buyer past the account step, still inside the accordion.
  enabled: boolean;
  phase: CheckoutPhase;
  openStep: CheckoutStep | null;
  values: CheckoutFormValues;
  // The save itself is injected (Checkout.tsx wires the API mutation) so the provider
  // stays renderable without a QueryClient — unit tests, Storybook.
  onSave?: (payload: SalesPortalCheckoutProgressWrite) => void;
}

// Mirrors the buyer's non-payment progress to the server (spec Item 11) so the
// abandoned-checkout job can email a resume link. Fires debounced on every change to
// the open step or the allowlisted fields; the PUT is an idempotent upsert, and a
// save that fails is simply retried by the next change — recovery is best-effort and
// must never get in the buyer's way.
export default function useCheckoutProgressSync({
  enabled,
  phase,
  openStep,
  values,
  onSave,
}: UseCheckoutProgressSyncArgs): void {
  // The step the buyer is on; a closed accordion (no open panel) means they have
  // completed everything and are at the Confirm-purchase button, i.e. Payment.
  const currentStep: CheckoutStep = openStep ?? 'payment';
  const payload = useMemo(() => buildCheckoutProgressPayload(values, currentStep), [values, currentStep]);
  const serialized = useMemo(() => JSON.stringify(payload), [payload]);
  const [debouncedSerialized] = useDebouncedValue(serialized, CHECKOUT_PROGRESS_SYNC_DEBOUNCE_MS);

  const lastSavedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !onSave || phase !== 'checkout') return;
    if (debouncedSerialized === lastSavedRef.current) return;

    lastSavedRef.current = debouncedSerialized;
    onSave(JSON.parse(debouncedSerialized) as SalesPortalCheckoutProgressWrite);
  }, [enabled, onSave, phase, debouncedSerialized]);
}
